/**
 * Connectivity event ring buffer. Records every online/offline transition of
 * ConnectivityService — with WHAT triggered it (native plugin event, DOM event,
 * poll probe, analyze-error nudge) and how long the probe took — so a tester
 * can SEE, after the fact, why the app flipped state late (or never).
 *
 * Persisted in localStorage (same reasoning as powersync/lifecycle-log.ts:
 * the reloads that accompany connectivity trouble wipe an in-memory buffer
 * exactly when it's needed). Surfaced in the DebugDiagnostics copy-all dump.
 */

export const CONN_LOG_KEY = 'zs_conn_log'
const MAX_ENTRIES = 40
const MAX_DETAIL_CHARS = 200

export type ConnLogEvent =
  | 'seed'          // initial value at construction (detail: "native=false" | "web=true")
  | 'plugin'        // @capacitor/network native availability (detail: "native" | "web-shim")
  | 'native-event'  // networkStatusChange fired (detail: "connected=false")
  | 'dom-event'     // window online/offline event (detail: "online" | "offline")
  | 'flip'          // _isOnline transitioned (detail: "→offline trigger=poll probe=fail 4002ms")
  | 'probe-error'   // a probe failed while already offline (detail: trigger + latency) — throttled by dedupe
  | 'probe-retry'   // first probe missed; retrying once before concluding offline (detail: trigger + latency)

export interface ConnLogEntry {
  ts: number
  event: ConnLogEvent
  detail?: string
}

export function logConn(event: ConnLogEvent, detail?: string): void {
  if (typeof window === 'undefined') return
  try {
    const entry: ConnLogEntry = {
      ts: Date.now(),
      event,
      ...(detail ? { detail: detail.slice(0, MAX_DETAIL_CHARS) } : {}),
    }
    const list = readConnLog()
    // Dedupe identical back-to-back events within 5s (repeated failing poll
    // probes while offline would otherwise flood the buffer).
    const last = list[list.length - 1]
    if (last && last.event === entry.event && last.detail === entry.detail && entry.ts - last.ts < 5000) return
    list.push(entry)
    while (list.length > MAX_ENTRIES) list.shift()
    try {
      window.localStorage.setItem(CONN_LOG_KEY, JSON.stringify(list))
    } catch {
      try { window.localStorage.setItem(CONN_LOG_KEY, JSON.stringify([entry])) } catch { /* give up */ }
    }
    console.log(`[CONN] ${event}${detail ? ` ${detail}` : ''}`)
  } catch {
    /* never let diagnostics throw */
  }
}

export function readConnLog(): ConnLogEntry[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(CONN_LOG_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

export function clearConnLog(): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(CONN_LOG_KEY) } catch { /* noop */ }
}
